import { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";

const DISMISS_KEY = "closet.iosInstall.dismissedAt";
const DISMISS_DAYS = 7;

/**
 * Subtle bottom banner shown only on iOS Safari (not already installed as PWA,
 * not inside the Capacitor native shell) to teach staff how to add the app to
 * their Home Screen. Dismissed for 7 days when the user taps X.
 */
export default function IosInstallBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Never show inside the Capacitor native shell — those users already have the app.
    if (window.Capacitor?.isNativePlatform?.()) return;

    // iOS detection — Safari on iPhone/iPad. iPadOS 13+ reports as Mac + touch.
    const ua = window.navigator.userAgent || "";
    const isIPhoneIPad = /iPad|iPhone|iPod/.test(ua);
    const isIPadOS = ua.includes("Mac") && "ontouchend" in document;
    const isIOS = isIPhoneIPad || isIPadOS;
    if (!isIOS) return;

    // Filter out non-Safari iOS browsers (Chrome/Firefox on iOS can't install PWAs).
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    if (!isSafari) return;

    // If already running as an installed PWA, hide.
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;
    if (isStandalone) return;

    // Respect the 7-day dismissal cooldown.
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400 * 1000) return;

    // Small delay so it doesn't fight the login page paint.
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  }

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-label="Install this app on your iPhone"
      data-testid="ios-install-banner"
      className="fixed left-3 right-3 z-[9998] pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
    >
      <div className="brut-card bg-[#FDF6EE] pointer-events-auto shadow-[6px_6px_0_0_rgba(0,0,0,0.9)] border-2 border-black">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-11 h-11 rounded-lg bg-[#7A3E1F] flex items-center justify-center">
              <img src="/apple-touch-icon-120.png" alt="" className="w-9 h-9 rounded-md" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Install · one-tap access</div>
              <div className="font-display font-black text-base leading-tight text-black">
                Add Closet Inventory to your Home Screen
              </div>
              <div className="mt-1.5 text-[13px] text-zinc-700 flex items-center gap-1 flex-wrap">
                <span>Tap</span>
                <Share size={14} className="inline text-[#0A84FF]" />
                <span className="font-bold">Share</span>
                <span>→</span>
                <span className="inline-flex items-center gap-1 font-bold">
                  <Plus size={13} className="inline" /> Add to Home Screen
                </span>
              </div>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss install prompt"
              data-testid="ios-install-dismiss-btn"
              className="shrink-0 w-8 h-8 -mr-1 -mt-1 flex items-center justify-center border-2 border-black bg-white hover:bg-[#F3E9DA] transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
        {/* Speech-bubble arrow pointing DOWN toward the Safari share icon */}
        <div
          aria-hidden
          className="absolute -bottom-[10px] left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-[#FDF6EE] border-b-2 border-r-2 border-black"
        />
      </div>
    </div>
  );
}
